import React, { useEffect, useState } from "react";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useFlags } from "launchdarkly-react-client-sdk";

import IconButton from "@src/sharedComponents/IconButton/IconButton";

import styles from "./DemoGuide.module.css";

const getIsProduction = (): boolean => {
  return window.location.hostname === "app.farther.com";
};

const getDemoSteps = (): string[] => {
  const isProduction = getIsProduction();

  if (isProduction) {
    // Production client IDs and specific demo flow
    return [
      "/Client/959139065260867587/Investments/TransitionProposals",
      "/Client/959139065260867587/Investments/TransitionProposals/TNP_DRKJ-4MDG-HBCN?pageIndex=2",
      "/Client/965032977264181250/Investments/PortfoliosV2",
      "/Advisor/Investments/ModelMarketplaceV2",
      "/Client/965032977264181250/TaxBudgets",
      "/Client/965032977264181250/Investments/PortfoliosV2/AIP_FAR_POFN5IN8-KFZQ-A8PJ-VA98-1UR6XRKL0508/Accounts",
      "/Client/965032977264181250/Investments/PortfoliosV2/AIP_FAR_POFN5IN8-KFZQ-A8PJ-VA98-1UR6XRKL0508/Assignments/CIP_JVM5-4T8V-UHRF/Do-not-sell",
      "/Client/959139065260867587/Sharing",
      "/Client/873879501562576897/Dashboard",
      "/Client/959139065260867587/Dashboard",
      "/Client/873879501562576897/Documents/Farther Records?sort=-created",
    ];
  } else {
    // UAT client IDs - Earl Denver, Grace Benjamin, Ruby Mae
    return [
      "/Client/936803730128764929/Investments/TransitionProposals",
      "/Client/936803730128764929/Investments/PortfoliosV2",
      "/Client/872182779886927874/Investments/PortfoliosV2",
      "/Advisor/Investments/ModelMarketplaceV2",
      "/Client/872182779886927874/TaxBudgets",
      "/Client/830321347435167747/Sharing",
      "/Client/936803730128764929/Dashboard",
      "/Client/872182779886927874/Dashboard",
      "/Client/830321347435167747/Dashboard",
      "/Client/936803730128764929/Documents/Farther Records?sort=-created",
    ];
  }
};

const DEMO_GUIDE_STEP_KEY = "demoGuideCurrentStep";

const DemoGuide: React.FC = () => {
  const { enableDemoGuide } = useFlags();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const demoSteps = getDemoSteps();

  useEffect(() => {
    const savedStep = localStorage.getItem(DEMO_GUIDE_STEP_KEY);
    if (savedStep) {
      const stepNumber = parseInt(savedStep, 10);
      if (stepNumber >= 0 && stepNumber < demoSteps.length) {
        setCurrentStep(stepNumber);
      }
    }

    const currentPath = window.location.pathname + window.location.search;
    const matchingStepIndex = demoSteps.findIndex((url) => url === currentPath);
    if (matchingStepIndex !== -1) {
      setCurrentStep(matchingStepIndex);
      localStorage.setItem(DEMO_GUIDE_STEP_KEY, matchingStepIndex.toString());
    }
  }, [demoSteps]);

  const navigateToStep = (stepIndex: number): void => {
    if (stepIndex >= 0 && stepIndex < demoSteps.length) {
      setCurrentStep(stepIndex);
      localStorage.setItem(DEMO_GUIDE_STEP_KEY, stepIndex.toString());
      window.location.href = demoSteps[stepIndex];
    }
  };

  const handlePrevious = (): void => {
    if (currentStep > 0) {
      navigateToStep(currentStep - 1);
    }
  };

  const handleNext = (): void => {
    if (currentStep < demoSteps.length - 1) {
      navigateToStep(currentStep + 1);
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === demoSteps.length - 1;

  if (!enableDemoGuide) {
    return null;
  }

  return (
    <div className={styles.demoGuide}>
      <span className={styles.stepIndicator}>
        {currentStep + 1}/{demoSteps.length}
      </span>
      <IconButton
        IconComponent={ArrowBackIosNewIcon}
        onClick={handlePrevious}
        disabled={isFirstStep}
        className={styles.arrowButton}
        iconClassName={styles.arrowIcon}
      />
      <IconButton
        IconComponent={ArrowForwardIosIcon}
        onClick={handleNext}
        disabled={isLastStep}
        className={styles.arrowButton}
        iconClassName={styles.arrowIcon}
      />
    </div>
  );
};

export default DemoGuide;
